import * as React from 'react';
import { useForm, UseFormProps, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { type AnyObjectSchema } from 'yup';
import Button from 'components/Button';
import { useTranslation } from 'next-i18next';
import { ProblemApiError } from '@poinz/api';
import { getProblemApiErrorMessage } from 'utils/api/errorParser';
import HelperText from 'components/form/HelperText';
import clsx from 'clsx';
import { LoadingButtonProps } from 'components/Button/LoadingButton';
import LoadingButton from 'components/Button/LoadingButton';

interface Props extends UseFormProps {
  children: React.ReactNode;
  buttonLabel?: string;
  initialValues: Record<string, any>;
  validationSchema?: AnyObjectSchema;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  cancelButtonLabel?: string;
  enableReinitialize?: boolean;
  error?: any;
  className?: string;
  formItemContainerClassName?: string;
  submitBtnClassName?: string;
  loadingButtonProps?: LoadingButtonProps;
}

const Form = ({
  children,
  buttonLabel,
  initialValues,
  validationSchema,
  onSubmit,
  enableReinitialize = false,
  error: propError,
  onCancel,
  cancelButtonLabel,
  className,
  formItemContainerClassName,
  submitBtnClassName,
  loadingButtonProps,
  ...other
}: Props) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(propError);
  const { t } = useTranslation();
  const methods = useForm({
    ...other,
    defaultValues: initialValues,
    resolver: validationSchema ? yupResolver(validationSchema) : undefined
  });
  const { handleSubmit, reset } = methods;

  React.useEffect(() => {
    if (propError !== error) {
      setError(propError);
    }
    // eslint-disable-next-line
  }, [propError]);

  const onSubmitHandler = async (data: any) => {
    setError('');
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (e: any) {
      if (e instanceof ProblemApiError) {
        setError(getProblemApiErrorMessage(e, t));
      } else {
        setError(e.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (enableReinitialize) {
      reset(initialValues);
    }
  }, [initialValues, enableReinitialize, reset]);

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmitHandler)}>
        <div className={clsx('flex flex-col gap-8', formItemContainerClassName)}>
          {children}

          {error && <HelperText error helperText={error} className="-mt-2 -mb-2 font-semibold" />}
          <div className={'flex justify-between flex-row-reverse'}>
            {loadingButtonProps ? (
              <LoadingButton variant="secondary" type="submit" {...loadingButtonProps} />
            ) : (
              <Button
                className={clsx('w-full', submitBtnClassName)}
                variant="secondary"
                type="submit"
                loading={isLoading}
              >
                {t(buttonLabel || 'general.form.save')}
              </Button>
            )}
            {cancelButtonLabel && typeof onCancel === 'function' && (
              <Button variant="ghost" type="button" onClick={onCancel}>
                {t(cancelButtonLabel)}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Form;
